/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { ButtonMenuItem } from '@atlaskit/navigation-system';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { FoldableType } from '../../../types';
import ItemWrapper from '../MenuItem/item-wrapper';

const styles = cssMap({
	textStyle: {
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),
		color: token('color.text.subtle'),
	},
});

export const showMoreMessages = defineMessages({
	showMore: {
		id: 'platform.navigation-experiences.universal-create.show-more',
		defaultMessage: 'More',
	},
	showLess: {
		id: 'platform.navigation-experiences.universal-create.show-less',
		defaultMessage: 'Less',
	},
	showMoreAriaLabel: {
		id: 'platform.navigation-experiences.universal-create.show-more.area-label',
		defaultMessage: `Show {text} {heading} options`,
	},
	toggleAriaLabel: {
		id: 'platform.navigation-experiences.universal-create.area-label',
		defaultMessage: `Toggle for {text}{heading} options`,
	},
});

export interface ShowMoreButtonProps {
	foldable?: FoldableType;
	heading?: string;
	onClick: () => void;
	showAll: boolean;
}

export default function ShowMoreButton({
	onClick,
	heading,
	showAll,
	foldable,
}: ShowMoreButtonProps) {
	const { formatMessage } = useIntl();
	const [expanded, setExpanded] = useState(false);
	const listItemRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// We render this button in a group, not a list, so we need to override the underlying component's semantics
		// because it wraps the button in a list item by default
		if (listItemRef.current) {
			listItemRef.current.removeAttribute('role');
		}
	}, []);

	const text = showAll
		? formatMessage(showMoreMessages.showLess)
		: formatMessage(showMoreMessages.showMore);

	const handleClick = useCallback(() => {
		setExpanded(true);
		onClick();
	}, [onClick]);

	// Hide the button if showAll is true or if it has been clicked (expanded)
	if (typeof foldable === 'number' && (showAll || expanded)) {
		return null;
	}

	return (
		<ItemWrapper isMenuItem={false}>
			<ButtonMenuItem
				elemBefore={<ShowMoreHorizontalIcon label="" color={token('color.text.subtle')} />}
				onClick={handleClick}
				testId="show-more-button"
				aria-label={formatMessage(showMoreMessages.showMoreAriaLabel, {
					text,
					heading: heading ? ` ${heading}` : '',
				})}
				listItemRef={listItemRef}
			>
				<Box as="span" xcss={styles.textStyle}>
					{text}
				</Box>
			</ButtonMenuItem>
		</ItemWrapper>
	);
}
